import React, { useCallback } from 'react';
import { Input, Upload, Button, Space, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

/**
 * Markdown text area that replaces aplanet-ui-kit's CustomTextArea.
 *
 * In read-only mode, renders markdown content.
 * In edit mode, provides a textarea with optional image upload.
 *
 * Props:
 *  - intl               (object)   react-intl intl object
 *  - value              (string)   Current markdown text
 *  - onChange            (function) Called with new text value
 *  - readOnly           (boolean)  Render as markdown (no editing)
 *  - placeholder        (string)   Placeholder text for textarea
 *  - supportImages      (boolean)  Show image upload button
 *  - imageUploadUrl     (string)   Upload endpoint URL
 *  - api_requests       (object)   Redux state with access_token
 *  - refreshAccessToken (function) Action to refresh access token
 */
const CustomTextArea = ({
  intl,
  value,
  onChange,
  readOnly,
  placeholder,
  supportImages,
  imageUploadUrl,
  api_requests,
  refreshAccessToken,
}) => {
  const handleTextChange = useCallback(
    (e) => {
      if (onChange) onChange(e.target.value);
    },
    [onChange]
  );

  const handleImageUpload = useCallback(
    async ({ file, onSuccess: onUploadSuccess, onError }) => {
      const doUpload = async (token) => {
        const formData = new FormData();
        formData.append('file', file);

        return fetch(imageUploadUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
      };

      try {
        let response = await doUpload(api_requests.access_token);

        if (response.status === 401 && refreshAccessToken) {
          refreshAccessToken();
          await new Promise(resolve => setTimeout(resolve, 1000));
          response = await doUpload(api_requests.access_token);
        }

        if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

        const data = await response.json();
        const imageUrl = data.url || data.download || '';

        if (imageUrl && onChange) {
          const imageMarkdown = `\n![image](${imageUrl})\n`;
          onChange((value || '') + imageMarkdown);
        }

        if (onUploadSuccess) onUploadSuccess(data);
      } catch (err) {
        if (onError) onError(err);
        message.error(
          intl?.formatMessage
            ? intl.formatMessage({ id: 'upload_error', defaultMessage: 'Image upload failed' })
            : 'Image upload failed'
        );
      }
    },
    [imageUploadUrl, api_requests, refreshAccessToken, onChange, value, intl]
  );

  if (readOnly) {
    return (
      <div className="CustomTextArea-readonly">
        <ReactMarkdown source={value} />
      </div>
    );
  }

  return (
    <div className="CustomTextArea">
      <Input.TextArea
        value={value}
        onChange={handleTextChange}
        placeholder={placeholder}
        autoSize={{ minRows: 4, maxRows: 16 }}
      />
      {supportImages && imageUploadUrl && (
        <Space style={{ marginTop: 8 }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={handleImageUpload}
          >
            <Button icon={<UploadOutlined />} size="small">
              {intl?.formatMessage
                ? intl.formatMessage({ id: 'upload_image', defaultMessage: 'Upload image' })
                : 'Upload image'}
            </Button>
          </Upload>
        </Space>
      )}
    </div>
  );
};

export default CustomTextArea;
